import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetCrmGoogleLocationMappingsApi } from './use-get-crm-google-location-mappings-api';

const endpoint = 'ums/v1/crm/locations/google/mappings';
export const updateCrmGoogleLocationMappingEndpointIdentifier = endpoint;

interface BodyType {
  googleLocationId: number;
  brandId: number;
  locationId: number;
  mappingConfidence: number;
  businessStatus: string;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  id: number;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateCrmGoogleLocationMappingApi(options?: Options): ReturnType {
  const { refetch: refetchGoogleLocationMappings } = useGetCrmGoogleLocationMappingsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ id, body }: TVariables) => {
    try {
      const { data } = await http.put(`${endpoint}/${id}`, body);

      // Refetch google location mappings list
      refetchGoogleLocationMappings();

      toast({
        description: 'Crm google location mapping has been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: endpoint, ...options });
}
